/**
 * @voltxampere/web — `<vxa-pane-settings>` (Sprint 18 Paket B B7).
 *
 * Mockup zenit-platform.html SEKME 5 (AYARLAR) page-level
 * karşılığı. Sticky sol navigasyon + altı alt-sekme:
 *
 *   I.   Görünüm        (tema, parlaklık, canvas, animasyon)
 *   II.  Dil & bölge    (TR ↔ EN — gerçek toggle)
 *   III. Simülasyon     (tolerans, max iterasyon — değerler salt-okunur)
 *   IV.  Klavye         (kısayol listesi)
 *   V.   Erişilebilirlik (yüksek kontrast, hareket azaltma)
 *   VI.  Hakkında       (sürüm, lisans, github)
 *
 * Sprint 18 Paket B'de "Dil" + "Hakkında" gerçek bağlantılı; diğer
 * tab'lar mockup'a uygun görsel iskelet — Sprint 19+'ta canvas
 * theme + tolerance settings backend'i geldiğinde gerçek toggle'a
 * çevrilecekler. UI shell hazır kalsın diye placeholder yerine
 * tam görselleri bırakıyoruz; "henüz aktif değil" durumunu hint
 * metni ile bildiriyoruz.
 *
 * Sub-tab durumu local component state — URL hash sub-route
 * Sprint 19'da eklenir (`#settings/appearance`).
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import { currentLocale, toggleLocale } from "../store/locale.js";
import { setPane } from "../store/router.js";
import { t, type TranslationKey } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";
import { VERSION } from "../version.js";
import "./vxa-settings-data.js";
// Sprint 23 D1+D2 — custom shortcut overrides + reset.
import {
  clearOverride,
  detectConflict,
  resetAllOverrides,
  setOverride,
  shortcutOverrides,
} from "../store/shortcut-overrides.js";
// Sprint 24 A3 — AYARLAR Görünüm toggle UI bağlama (canvas-prefs).
import {
  canvasStyle,
  setCanvasStyle,
  setSymbolStandard,
  setWireCurve,
  symbolStandard,
  wireCurve,
  type CanvasStyle,
  type SymbolStandard,
  type WireCurve,
} from "../store/canvas-prefs.js";
// Sprint 29-5 — fx animation toggle (current flow + wire glow).
// Sprint 30-5 — voltage heatmap + wire gradient toggles.
import {
  fxAnimationEnabled,
  setFxAnimationEnabled,
  setVoltageGradientEnabled,
  setVoltageHeatmapEnabled,
  voltageGradientEnabled,
  voltageHeatmapEnabled,
} from "../store/fx-state.js";
// Sprint 31-6 — audio mute + master volume + autoplay info chip.
import {
  audioAutoplayHinted,
  audioMasterVolume,
  audioMuted,
  dismissAutoplayHint,
  ensureAudioEngine,
  setAudioMasterVolume,
  setAudioMuted,
} from "../store/audio-state.js";
// Sprint 33-4 — Sürekli Akış toggles (always-live + idle detection).
import { alwaysLiveEnabledSignal } from "../store/always-live-state.js";
import { idleDetectionEnabledSignal } from "../store/idle-state.js";
import { setAlwaysLiveEnabled, setIdleDetectionEnabled } from "../store/live-mode-prefs.js";
// Sprint 34-5 — Termal görüntüleme toggle (canvas heatmap overlay).
import { setThermalEnabled, thermalEnabledSignal } from "../store/thermal-prefs.js";
// Sprint 80.Z B1 — Erişilebilirlik yüksek-kontrast signal binding
// (closes 14-month Sprint 24 backlog).
import {
  highContrast,
  reducedMotion,
  setHighContrast,
  setReducedMotion,
} from "../store/a11y-state.js";
// Sprint 80.Z L-AYARLAR-1 — font size signal binding (AYARLAR
// audit Phase A finding closure: 4 placeholder buttons).
import { fontSize, setFontSize, FONT_SIZES } from "../store/font-size-state.js";
// Sprint 35-5 — Patlama Efektleri toggles (master + smoke separate).
import {
  explosionEnabledSignal,
  explosionSmokeEnabledSignal,
  setExplosionEnabled,
  setExplosionSmokeEnabled,
} from "../store/explosion-prefs.js";
// Sprint 36-5 — Hasar (damage persistence + wire overload) toggles.
import {
  damagePersistAcrossReloadSignal,
  setDamagePersistAcrossReload,
  setWireOverloadEnabled,
  wireOverloadEnabledSignal,
} from "../store/damage-prefs.js";
// Sprint 37-4 — Hasar Sesleri (8 damage sound effects toggle).
import { damageAudioEnabledSignal, setDamageAudioEnabled } from "../store/damage-audio-prefs.js";
// Sprint 24 A3 — AYARLAR Simülasyon form UI bağlama (sim-prefs).
import {
  acDefaults,
  resetSimPrefs,
  setACDefaults,
  setTemperatureCelsius,
  setTransientDefaults,
  temperatureCelsius,
  temperatureK,
  transientDefaults,
  TEMPERATURE_MAX_C,
  TEMPERATURE_MIN_C,
} from "../store/sim-prefs.js";
import type { ACDefaults } from "@voltxampere/persistence";
// Sprint 53 — AYARLAR Simülasyon default-speed entry.
import {
  formatSpeedLabel,
  playbackSpeedSignal,
  setPlaybackSpeed,
  SPEED_PRESETS,
} from "../store/playback-speed.js";

type SettingsSubTab =
  | "appearance"
  | "language"
  | "simulation"
  | "shortcuts"
  | "a11y"
  | "data"
  | "about";

const SUBTABS: readonly { id: SettingsSubTab; labelKey: TranslationKey }[] = [
  { id: "appearance", labelKey: "settings.tab.appearance" },
  { id: "language", labelKey: "settings.tab.language" },
  { id: "simulation", labelKey: "settings.tab.simulation" },
  { id: "shortcuts", labelKey: "settings.tab.shortcuts" },
  { id: "a11y", labelKey: "settings.tab.a11y" },
  // Sprint 21 C5 — Veri sekmesi (D-21-3 free-form, mockup yok).
  { id: "data", labelKey: "settings.tab.data" },
  { id: "about", labelKey: "settings.tab.about" },
];

@customElement("vxa-pane-settings")
export class VxaPaneSettings extends LitElement {
  private readonly _locale = new SignalController(this, currentLocale);
  // Sprint 23 D1 — re-render when override map changes.
  private readonly _overrides = new SignalController(this, shortcutOverrides);
  // Sprint 24 A3 — re-render when theme prefs change.
  private readonly _canvasStyle = new SignalController(this, canvasStyle);
  private readonly _wireCurve = new SignalController(this, wireCurve);
  private readonly _symbolStandard = new SignalController(this, symbolStandard);
  // Sprint 29-5 — re-render when fx animation toggle flips.
  private readonly _fxAnimation = new SignalController(this, fxAnimationEnabled);
  // Sprint 30-5 — re-render when heatmap / gradient toggles flip.
  private readonly _heatmap = new SignalController(this, voltageHeatmapEnabled);
  private readonly _gradient = new SignalController(this, voltageGradientEnabled);
  // Sprint 31-6 — re-render when audio toggles / slider flip.
  private readonly _audioMuted = new SignalController(this, audioMuted);
  private readonly _audioVolume = new SignalController(this, audioMasterVolume);
  private readonly _audioHinted = new SignalController(this, audioAutoplayHinted);
  // Sprint 80.Z B1 — Erişilebilirlik yüksek-kontrast (Sprint 24 backlog).
  private readonly _highContrast = new SignalController(this, highContrast);
  // Sprint 80.Z L-AYARLAR-1 — font size selection.
  private readonly _fontSize = new SignalController(this, fontSize);
  // Sprint 80.Z L-AYARLAR-2 — reduced-motion toggle.
  private readonly _reducedMotion = new SignalController(this, reducedMotion);
  // Sprint 33-4 — re-render when Sürekli Akış toggles flip.
  private readonly _alwaysLive = new SignalController(this, alwaysLiveEnabledSignal);
  private readonly _idleDetection = new SignalController(this, idleDetectionEnabledSignal);
  // Sprint 34-5 — re-render when termal görüntüleme toggle flips.
  private readonly _thermalEnabled = new SignalController(this, thermalEnabledSignal);
  // Sprint 35-5 — re-render when explosion toggles flip.
  private readonly _explosionEnabled = new SignalController(this, explosionEnabledSignal);
  private readonly _explosionSmoke = new SignalController(this, explosionSmokeEnabledSignal);
  // Sprint 36-5 — re-render when damage toggles flip.
  private readonly _damagePersist = new SignalController(this, damagePersistAcrossReloadSignal);
  private readonly _wireOverload = new SignalController(this, wireOverloadEnabledSignal);
  // Sprint 37-4 — re-render when damage sound toggle flips.
  private readonly _damageAudio = new SignalController(this, damageAudioEnabledSignal);
  // Sprint 24 A3 — re-render when sim defaults change.
  private readonly _ac = new SignalController(this, acDefaults);
  private readonly _tran = new SignalController(this, transientDefaults);
  private readonly _temp = new SignalController(this, temperatureK);
  // Sprint 53 — re-render when playback speed changes.
  private readonly _playbackSpeed = new SignalController(this, playbackSpeedSignal);

  @state()
  private _subtab: SettingsSubTab = "appearance";

  static override styles = css`
    :host {
      display: block;
      height: 100%;
      overflow-y: auto;
      overflow-x: hidden;
      background: var(--void);
      color: var(--ivory);
    }

    .wrap {
      max-width: 1100px;
      margin: 0 auto;
      padding: 60px var(--s-9) 120px;
      display: grid;
      grid-template-columns: 220px 1fr;
      gap: 80px;
    }

    /* ─────────────────── NAV ─────────────────── */
    .nav {
      position: sticky;
      top: 24px;
      align-self: flex-start;
    }

    .nav-label {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.3em;
      color: var(--brass);
      text-transform: uppercase;
      margin-bottom: 20px;
    }

    .nav-list {
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    .nav-item {
      padding: 12px 16px;
      font-family: var(--font-body);
      font-size: 16px;
      font-style: italic;
      color: var(--ivory-dim);
      cursor: pointer;
      border-left: 2px solid transparent;
      transition:
        color 180ms,
        border-color 180ms,
        padding 180ms;
      background: transparent;
      border-top: none;
      border-right: none;
      border-bottom: none;
      text-align: left;
    }

    .nav-item:hover {
      color: var(--ivory);
    }

    .nav-item[aria-current="page"] {
      color: var(--brass);
      border-left-color: var(--brass);
      padding-left: 20px;
    }

    .nav-item:focus-visible {
      outline: 1px solid var(--brass);
      outline-offset: -1px;
    }

    /* ─────────────────── CONTENT ─────────────────── */
    .content-title {
      font-family: var(--font-display);
      font-weight: 300;
      font-size: 48px;
      color: var(--ivory);
      line-height: 1;
      margin: 0 0 12px;
      letter-spacing: -0.015em;
    }

    .content-sub {
      font-size: 16px;
      color: var(--ivory-dim);
      font-style: italic;
      margin: 0 0 48px;
      max-width: 640px;
    }

    .group {
      margin-bottom: 48px;
    }

    .group-title {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.3em;
      color: var(--brass);
      text-transform: uppercase;
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .group-title::after {
      content: "";
      flex: 1;
      height: 1px;
      background: var(--ivory-wisp);
    }

    .row {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 24px;
      align-items: center;
      padding: 18px 0;
      border-bottom: 1px dotted var(--ink-3);
    }

    .row:last-child {
      border-bottom: none;
    }

    .row .name {
      font-family: var(--font-display);
      font-size: 18px;
      color: var(--ivory);
      letter-spacing: -0.005em;
      margin-bottom: 4px;
    }

    .row .desc {
      font-family: var(--font-body);
      font-style: italic;
      font-size: 14px;
      color: var(--ivory-dim);
    }

    /* ─────────────────── CONTROLS ─────────────────── */
    .toggle-group {
      display: flex;
      gap: 2px;
      background: var(--ink-3);
      padding: 2px;
      border: 1px solid var(--ink-3);
    }

    .toggle-opt {
      padding: 8px 14px;
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.15em;
      color: var(--ivory-dim);
      background: transparent;
      border: none;
      cursor: pointer;
      text-transform: uppercase;
      transition:
        background 160ms,
        color 160ms;
    }

    .toggle-opt:hover {
      color: var(--ivory);
    }

    .toggle-opt[aria-pressed="true"] {
      background: var(--brass);
      color: var(--void);
      font-weight: 500;
    }

    .theme-swatches {
      display: flex;
      gap: 8px;
    }

    .theme-sw {
      width: 48px;
      height: 32px;
      border: 1px solid var(--ink-3);
      cursor: pointer;
      transition:
        border-color 180ms,
        box-shadow 180ms;
      background: linear-gradient(135deg, var(--void) 50%, var(--brass) 50%);
    }

    .theme-sw:hover {
      border-color: var(--brass-dim);
    }

    .theme-sw[aria-pressed="true"] {
      border-color: var(--brass);
      box-shadow: 0 0 14px rgba(192, 138, 62, 0.4);
    }

    .tsw-observatory {
      background: linear-gradient(135deg, #0a1428 50%, #c9a227 50%);
    }

    .tsw-blueprint {
      background: linear-gradient(135deg, #e8f0f5 50%, #0a2540 50%);
    }

    .tsw-paper {
      background: linear-gradient(135deg, #f4efe1 50%, #2b1d0e 50%);
    }

    /* slider — placeholder visual; B7 read-only, Sprint 19'da
       gerçek input range'a çevrilecek. */
    .slider-wrap {
      width: 220px;
      display: grid;
      grid-template-columns: 1fr 40px;
      gap: 12px;
      align-items: center;
    }

    .slider-track {
      position: relative;
      height: 2px;
      background: var(--ink-3);
    }

    .slider-fill {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      background: var(--brass);
      width: 65%;
    }

    .slider-knob {
      position: absolute;
      left: 65%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 14px;
      height: 14px;
      background: var(--brass);
      border-radius: 50%;
      box-shadow: 0 0 12px var(--brass);
    }

    .slider-value {
      font-family: var(--font-mono);
      font-size: 12px;
      color: var(--brass);
      text-align: right;
    }

    /* shortcut list */
    .shortcut-row {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 24px;
      padding: 12px 0;
      border-bottom: 1px dotted var(--ink-3);
    }

    .shortcut-row:last-child {
      border-bottom: none;
    }

    .shortcut-name {
      font-family: var(--font-body);
      font-size: 15px;
      color: var(--ivory);
    }

    .shortcut-keys {
      font-family: var(--font-mono);
      font-size: 11px;
      letter-spacing: 0.18em;
      color: var(--brass);
      border: 1px solid var(--brass-faint);
      padding: 4px 10px;
    }

    /* Sprint 24 A3 — number input control for sim defaults form. */
    .num-input {
      font-family: var(--font-mono);
      font-size: 12px;
      letter-spacing: 0.06em;
      color: var(--ivory);
      background: var(--ink-3);
      border: 1px solid var(--ivory-wisp);
      padding: 6px 10px;
      width: 140px;
      text-align: right;
      transition:
        border-color 160ms,
        background 160ms;
    }

    .num-input:hover {
      border-color: var(--brass-faint);
    }

    .num-input:focus-visible {
      outline: 1px solid var(--brass);
      outline-offset: -1px;
      border-color: var(--brass);
    }

    .num-input::-webkit-inner-spin-button,
    .num-input::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Sprint 53 — playback speed select, mirrors .num-input but for
       <select> elements. Width is narrower because the options are
       short (1×, 10×, etc). */
    .select-input {
      font-family: var(--font-mono);
      font-size: 12px;
      letter-spacing: 0.06em;
      color: var(--ivory);
      background: var(--ink-3);
      border: 1px solid var(--ivory-wisp);
      padding: 6px 10px;
      width: 96px;
      text-align: right;
      cursor: pointer;
      transition:
        border-color 160ms,
        background 160ms;
    }

    .select-input:hover {
      border-color: var(--brass-faint);
    }

    .select-input:focus-visible {
      outline: 1px solid var(--brass);
      outline-offset: -1px;
      border-color: var(--brass);
    }

    /* about */
    .about-card {
      padding: 32px;
      background: var(--ink);
      border: 1px solid var(--ivory-wisp);
      margin-bottom: 24px;
    }

    .about-card-label {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.3em;
      color: var(--brass);
      text-transform: uppercase;
      margin-bottom: 12px;
    }

    .about-card-title {
      font-family: var(--font-display);
      font-size: 32px;
      color: var(--ivory);
      margin-bottom: 8px;
      letter-spacing: -0.01em;
    }

    .about-card-body {
      font-family: var(--font-body);
      font-size: 15px;
      color: var(--ivory-dim);
      font-style: italic;
      line-height: 1.6;
    }

    .about-card-body em {
      color: var(--brass);
      font-style: italic;
    }

    .deferred {
      font-family: var(--font-body);
      font-style: italic;
      font-size: 13px;
      color: var(--ivory-faint);
      margin-top: var(--s-3);
    }

    /* ─────────────────── RESPONSIVE ─────────────────── */
    @media (max-width: 768px) {
      .wrap {
        grid-template-columns: 1fr;
        gap: 32px;
      }

      .nav {
        position: static;
      }

      .nav-list {
        flex-direction: row;
        gap: 0;
        overflow-x: auto;
        border-bottom: 1px solid var(--ivory-wisp);
      }

      .nav-item {
        border-left: none;
        border-bottom: 2px solid transparent;
        padding: 12px;
      }

      .nav-item[aria-current="page"] {
        padding-left: 12px;
        border-left: none;
        border-bottom-color: var(--brass);
      }

      .row {
        grid-template-columns: 1fr;
        gap: 12px;
      }

      .content-title {
        font-size: 36px;
      }
    }
  `;

  override render() {
    return html`<div class="wrap">${this.renderNav()} ${this.renderContent()}</div>`;
  }

  private renderNav() {
    return html`<aside class="nav">
      <div class="nav-label">${t("settings.nav.label")}</div>
      <div class="nav-list" role="tablist">
        ${SUBTABS.map(
          (s) =>
            html`<button
              class="nav-item"
              type="button"
              role="tab"
              data-subtab=${s.id}
              data-testid=${`settings-tab-${s.id}`}
              aria-current=${this._subtab === s.id ? "page" : "false"}
              title=${t(s.labelKey)}
              @click=${() => {
                this._select(s.id);
              }}
            >
              ${t(s.labelKey)}
            </button>`,
        )}
      </div>
    </aside>`;
  }

  private renderContent() {
    switch (this._subtab) {
      case "appearance":
        return this.renderAppearance();
      case "language":
        return this.renderLanguage();
      case "simulation":
        return this.renderSimulation();
      case "shortcuts":
        return this.renderShortcuts();
      case "a11y":
        return this.renderA11y();
      case "data":
        return this.renderData();
      case "about":
        return this.renderAbout();
    }
  }

  private renderData() {
    return html`<vxa-settings-data data-testid="content-data"></vxa-settings-data>`;
  }

  private renderAppearance() {
    return html`<div data-testid="content-appearance">
      <h1 class="content-title">${t("settings.appearance.title")}</h1>
      <p class="content-sub">${t("settings.appearance.sub")}</p>

      <div class="group">
        <div class="group-title">${t("settings.appearance.theme.label")}</div>
        ${this.renderRow(
          t("settings.appearance.theme.row.title"),
          t("settings.appearance.theme.row.desc"),
          html`<div class="theme-swatches" role="group" aria-label="Tema seçimi">
            <button
              class="theme-sw"
              type="button"
              aria-pressed="true"
              aria-label="Tema: Zenit"
              title="Zenit"
            ></button>
            <button
              class="theme-sw tsw-observatory"
              type="button"
              aria-pressed="false"
              aria-label="Tema: Observatory"
              title="Observatory"
            ></button>
            <button
              class="theme-sw tsw-blueprint"
              type="button"
              aria-pressed="false"
              aria-label="Tema: Blueprint"
              title="Blueprint"
            ></button>
            <button
              class="theme-sw tsw-paper"
              type="button"
              aria-pressed="false"
              aria-label="Tema: Paper"
              title="Paper"
            ></button>
          </div>`,
        )}
        ${this.renderRow(
          t("settings.appearance.brightness.row.title"),
          t("settings.appearance.brightness.row.desc"),
          html`<div class="slider-wrap" aria-disabled="true">
            <div class="slider-track">
              <div class="slider-fill"></div>
              <div class="slider-knob"></div>
            </div>
            <div class="slider-value">65%</div>
          </div>`,
        )}
      </div>

      <div class="group">
        <div class="group-title">${t("settings.appearance.canvas.label")}</div>
        ${this.renderRow(
          t("settings.appearance.canvas.background.title"),
          t("settings.appearance.canvas.background.desc"),
          this.renderCanvasStyleToggle(),
        )}
        ${this.renderRow(
          t("settings.appearance.canvas.wire.title"),
          t("settings.appearance.canvas.wire.desc"),
          this.renderWireCurveToggle(),
        )}
        ${this.renderRow(
          t("settings.appearance.canvas.symbol.title"),
          t("settings.appearance.canvas.symbol.desc"),
          this.renderSymbolStandardToggle(),
        )}
        ${this.renderRow(
          t("settings.appearance.canvas.fx.title"),
          t("settings.appearance.canvas.fx.desc"),
          this.renderFxAnimationToggle(),
        )}
        ${this.renderRow(
          t("settings.appearance.canvas.heatmap.title"),
          t("settings.appearance.canvas.heatmap.desc"),
          this.renderHeatmapToggle(),
        )}
        ${this.renderRow(
          t("settings.appearance.canvas.gradient.title"),
          t("settings.appearance.canvas.gradient.desc"),
          this.renderGradientToggle(),
        )}
      </div>

      <!-- Sprint 31-6 — Ses bölümü (master volume + mute + autoplay hint). -->
      <div class="group">
        <div class="group-title">${t("settings.audio.title")}</div>
        ${this.renderAutoplayHint()}
        ${this.renderRow(
          t("settings.audio.masterVolume"),
          t("settings.audio.masterVolume.desc"),
          this.renderVolumeSlider(),
        )}
        ${this.renderRow(
          t("settings.audio.muted"),
          t("settings.audio.muted.desc"),
          this.renderMuteToggle(),
        )}
      </div>

      <!-- Sprint 33-4 — Sürekli Akış (always-live + idle detection). -->
      <div class="group">
        <div class="group-title">${t("settings.liveMode.title")}</div>
        <div class="group-sub">${t("settings.liveMode.sub")}</div>
        ${this.renderRow(
          t("settings.liveMode.alwaysLive"),
          t("settings.liveMode.alwaysLive.desc"),
          this.renderAlwaysLiveToggle(),
        )}
        ${this.renderRow(
          t("settings.liveMode.idleDetection"),
          t("settings.liveMode.idleDetection.desc"),
          this.renderIdleDetectionToggle(),
        )}
      </div>

      <!-- Sprint 34-5 — Termal görüntüleme (canvas heatmap overlay). -->
      <div class="group">
        <div class="group-title">${t("settings.thermal.title")}</div>
        <div class="group-sub">${t("settings.thermal.sub")}</div>
        ${this.renderRow(
          t("settings.thermal.visualization"),
          t("settings.thermal.visualization.desc"),
          this.renderThermalVisToggle(),
        )}
      </div>

      <!-- Sprint 35-5 — Patlama Efektleri (LED + cap + resistor + smoke). -->
      <div class="group">
        <div class="group-title">${t("settings.explosion.title")}</div>
        <div class="group-sub">${t("settings.explosion.sub")}</div>
        ${this.renderRow(
          t("settings.explosion.enabled"),
          t("settings.explosion.enabled.desc"),
          this.renderExplosionEnabledToggle(),
        )}
        ${this.renderRow(
          t("settings.explosion.smokeEnabled"),
          t("settings.explosion.smokeEnabled.desc"),
          this.renderExplosionSmokeToggle(),
        )}
      </div>

      <!-- Sprint 36-5 — Hasar (kalıcı hasar + kablo aşırı yük). -->
      <div class="group">
        <div class="group-title">${t("settings.damage.title")}</div>
        <div class="group-sub">${t("settings.damage.sub")}</div>
        ${this.renderRow(
          t("settings.damage.persistAcrossReload"),
          t("settings.damage.persistAcrossReload.desc"),
          this.renderDamagePersistToggle(),
        )}
        ${this.renderRow(
          t("settings.damage.wireOverloadEnabled"),
          t("settings.damage.wireOverloadEnabled.desc"),
          this.renderWireOverloadToggle(),
        )}
      </div>

      <!-- Sprint 37-4 — Hasar Sesleri (8 damage sound effects). -->
      <div class="group">
        <div class="group-title">${t("settings.damageAudio.title")}</div>
        <div class="group-sub">${t("settings.damageAudio.sub")}</div>
        ${this.renderRow(
          t("settings.damageAudio.enabled"),
          t("settings.damageAudio.enabled.desc"),
          this.renderDamageAudioToggle(),
        )}
      </div>
    </div>`;
  }

  /**
   * Sprint 37-4 — damage sound effects master toggle. When OFF, the
   * dispatcher silences itself and sustained loops drain immediately.
   */
  private renderDamageAudioToggle() {
    const enabled = this._damageAudio.value;
    return html`<div class="toggle-group" role="group">
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${!enabled}
        data-testid="damage-audio-off"
        @click=${(): void => {
          setDamageAudioEnabled(false);
        }}
      >
        ${t("settings.damageAudio.enabled.option.off")}
      </button>
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${enabled}
        data-testid="damage-audio-on"
        @click=${(): void => {
          setDamageAudioEnabled(true);
        }}
      >
        ${t("settings.damageAudio.enabled.option.on")}
      </button>
    </div>`;
  }

  /**
   * Sprint 36-5 — damage persistence master toggle. When OFF, the
   * damage snapshot is cleared on persist + reload starts fresh.
   */
  private renderDamagePersistToggle() {
    const enabled = this._damagePersist.value;
    return html`<div class="toggle-group" role="group">
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${!enabled}
        data-testid="damage-persist-off"
        @click=${(): void => {
          setDamagePersistAcrossReload(false);
        }}
      >
        ${t("settings.damage.persistAcrossReload.option.off")}
      </button>
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${enabled}
        data-testid="damage-persist-on"
        @click=${(): void => {
          setDamagePersistAcrossReload(true);
        }}
      >
        ${t("settings.damage.persistAcrossReload.option.on")}
      </button>
    </div>`;
  }

  /**
   * Sprint 36-5 — wire overload detection master toggle. When OFF,
   * wires render normally regardless of current.
   */
  private renderWireOverloadToggle() {
    const enabled = this._wireOverload.value;
    return html`<div class="toggle-group" role="group">
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${!enabled}
        data-testid="wire-overload-off"
        @click=${(): void => {
          setWireOverloadEnabled(false);
        }}
      >
        ${t("settings.damage.wireOverloadEnabled.option.off")}
      </button>
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${enabled}
        data-testid="wire-overload-on"
        @click=${(): void => {
          setWireOverloadEnabled(true);
        }}
      >
        ${t("settings.damage.wireOverloadEnabled.option.on")}
      </button>
    </div>`;
  }

  /**
   * Sprint 35-5 — explosion master toggle. Wires the
   * `explosionEnabledSignal` to a binary on/off switch. When OFF
   * the canvas overlay (Sprint 36+) skips all LED + cap + resistor
   * explosion animations.
   */
  private renderExplosionEnabledToggle() {
    const enabled = this._explosionEnabled.value;
    return html`<div class="toggle-group" role="group">
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${!enabled}
        data-testid="explosion-enabled-off"
        @click=${(): void => {
          setExplosionEnabled(false);
        }}
      >
        ${t("settings.explosion.enabled.option.off")}
      </button>
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${enabled}
        data-testid="explosion-enabled-on"
        @click=${(): void => {
          setExplosionEnabled(true);
        }}
      >
        ${t("settings.explosion.enabled.option.on")}
      </button>
    </div>`;
  }

  /**
   * Sprint 35-5 — smoke particles separate toggle. Perf-conscious
   * users may keep explosions but skip smoke (smoke pool is the
   * heaviest visual surface — 200 particle cap + per-frame opacity
   * decay + size expansion).
   */
  private renderExplosionSmokeToggle() {
    const enabled = this._explosionSmoke.value;
    return html`<div class="toggle-group" role="group">
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${!enabled}
        data-testid="explosion-smoke-off"
        @click=${(): void => {
          setExplosionSmokeEnabled(false);
        }}
      >
        ${t("settings.explosion.smokeEnabled.option.off")}
      </button>
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${enabled}
        data-testid="explosion-smoke-on"
        @click=${(): void => {
          setExplosionSmokeEnabled(true);
        }}
      >
        ${t("settings.explosion.smokeEnabled.option.on")}
      </button>
    </div>`;
  }

  /**
   * Sprint 34-5 — termal görüntüleme toggle. Wires the
   * `thermalEnabledSignal` (Sprint 34-3 overlay master switch) to a
   * binary on/off switch. Persists via `setThermalEnabled`
   * (thermal-prefs).
   */
  private renderThermalVisToggle() {
    const enabled = this._thermalEnabled.value;
    return html`<div class="toggle-group" role="group">
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${!enabled}
        data-testid="thermal-vis-off"
        @click=${(): void => {
          setThermalEnabled(false);
        }}
      >
        ${t("settings.thermal.visualization.option.off")}
      </button>
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${enabled}
        data-testid="thermal-vis-on"
        @click=${(): void => {
          setThermalEnabled(true);
        }}
      >
        ${t("settings.thermal.visualization.option.on")}
      </button>
    </div>`;
  }

  /**
   * Sprint 33-4 — always-live toggle. Wires the
   * `alwaysLiveEnabledSignal` (Sprint 33-1) to a binary on/off
   * switch. Persists via `setAlwaysLiveEnabled` (live-mode-prefs).
   */
  private renderAlwaysLiveToggle() {
    const enabled = this._alwaysLive.value;
    return html`<div class="toggle-group" role="group">
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${!enabled}
        data-testid="always-live-off"
        @click=${(): void => {
          setAlwaysLiveEnabled(false);
        }}
      >
        ${t("settings.liveMode.alwaysLive.option.off")}
      </button>
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${enabled}
        data-testid="always-live-on"
        @click=${(): void => {
          setAlwaysLiveEnabled(true);
        }}
      >
        ${t("settings.liveMode.alwaysLive.option.on")}
      </button>
    </div>`;
  }

  /**
   * Sprint 33-4 — idle detection toggle. Wires the
   * `idleDetectionEnabledSignal` (Sprint 33-2) to a binary on/off
   * switch. When OFF, the idle timer never schedules so RAF stays
   * at 60 fps continuously.
   */
  private renderIdleDetectionToggle() {
    const enabled = this._idleDetection.value;
    return html`<div class="toggle-group" role="group">
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${!enabled}
        data-testid="idle-detection-off"
        @click=${(): void => {
          setIdleDetectionEnabled(false);
        }}
      >
        ${t("settings.liveMode.idleDetection.option.off")}
      </button>
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${enabled}
        data-testid="idle-detection-on"
        @click=${(): void => {
          setIdleDetectionEnabled(true);
        }}
      >
        ${t("settings.liveMode.idleDetection.option.on")}
      </button>
    </div>`;
  }

  /**
   * Sprint 31-6 — first-run autoplay info chip. Shown until user
   * dismisses (or AudioContext init succeeds via Run/F5). Browser
   * autoplay policies require a user gesture to start audio output —
   * this chip explains why audio doesn't start at page load.
   */
  private renderAutoplayHint() {
    if (this._audioHinted.value) return null;
    return html`<div
      class="autoplay-hint"
      role="status"
      style="background: var(--ink); border: 1px solid var(--ivory-wisp); padding: var(--s-3); margin-bottom: var(--s-3); font-family: var(--font-body); font-size: var(--fs-small); color: var(--ivory-dim);"
    >
      <div style="margin-bottom: var(--s-2);">${t("settings.audio.autoplay.hint")}</div>
      <button
        type="button"
        @click=${(): void => {
          dismissAutoplayHint();
          void ensureAudioEngine();
        }}
        style="background: var(--brass-faint); color: var(--ink); border: 1px solid var(--brass); padding: var(--s-1) var(--s-3); cursor: pointer; font-family: var(--font-mono); font-size: var(--fs-mono-tiny); letter-spacing: 0.18em; text-transform: uppercase;"
      >
        ${t("settings.audio.autoplay.dismiss")}
      </button>
    </div>`;
  }

  /**
   * Sprint 31-6 — master volume slider 0..1. Drag triggers Audio
   * Context lazy-init so the user's first slider interaction also
   * counts as the audio-enabling gesture.
   */
  private renderVolumeSlider() {
    const value = this._audioVolume.value;
    return html`<input
      type="range"
      min="0"
      max="1"
      step="0.01"
      .value=${String(value)}
      data-testid="audio-volume-slider"
      style="width: 200px;"
      @input=${(e: Event): void => {
        const target = e.target as HTMLInputElement;
        const next = Number(target.value);
        if (Number.isFinite(next)) {
          setAudioMasterVolume(next);
          void ensureAudioEngine();
        }
      }}
    />`;
  }

  /**
   * Sprint 31-6 — mute toggle. Same toggle-opt pattern as Sprint 29
   * fx animation. When muted, the AudioFrame guard returns empty so
   * the Tone.js engine ramps every gain to zero.
   */
  private renderMuteToggle() {
    const muted = this._audioMuted.value;
    return html`<div class="toggle-group" role="group">
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${!muted}
        data-testid="audio-mute-off"
        @click=${(): void => {
          setAudioMuted(false);
        }}
      >
        ${t("settings.audio.muted.option.off")}
      </button>
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${muted}
        data-testid="audio-mute-on"
        @click=${(): void => {
          setAudioMuted(true);
        }}
      >
        ${t("settings.audio.muted.option.on")}
      </button>
    </div>`;
  }

  /**
   * Sprint 30-5 — voltage heatmap (per-node radial halos) on/off
   * toggle. STATIC overlay so reduced-motion does NOT auto-disable
   * (heatmap is a visual cue, not motion). Independent from gradient.
   */
  private renderHeatmapToggle() {
    const current = this._heatmap.value;
    return html`<div class="toggle-group" role="group">
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${current}
        data-testid="voltage-heatmap-on"
        @click=${() => {
          setVoltageHeatmapEnabled(true);
        }}
      >
        ${t("settings.appearance.canvas.heatmap.option.on")}
      </button>
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${!current}
        data-testid="voltage-heatmap-off"
        @click=${() => {
          setVoltageHeatmapEnabled(false);
        }}
      >
        ${t("settings.appearance.canvas.heatmap.option.off")}
      </button>
    </div>`;
  }

  /**
   * Sprint 30-5 — wire color gradient stroke on/off toggle. Independent
   * from heatmap so users can opt to one without the other. STATIC
   * overlay (reduced-motion does not auto-disable).
   */
  private renderGradientToggle() {
    const current = this._gradient.value;
    return html`<div class="toggle-group" role="group">
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${current}
        data-testid="voltage-gradient-on"
        @click=${() => {
          setVoltageGradientEnabled(true);
        }}
      >
        ${t("settings.appearance.canvas.gradient.option.on")}
      </button>
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${!current}
        data-testid="voltage-gradient-off"
        @click=${() => {
          setVoltageGradientEnabled(false);
        }}
      >
        ${t("settings.appearance.canvas.gradient.option.off")}
      </button>
    </div>`;
  }

  /**
   * Sprint 29-5 — fx (current flow + wire glow) on/off toggle. Two
   * options matching the existing sub-tab toggle pattern. The user's
   * choice flips an in-memory signal; persistence to the settings DB
   * arrives with Sprint 30 alongside the heatmap toggle so the same
   * `theme` JSON write captures both new fields together.
   *
   * The system-level `prefers-reduced-motion: reduce` preference
   * unconditionally overrides this toggle (caller-side guard in
   * `fx-state.ts` short-circuits even when the user's toggle is on).
   */
  private renderFxAnimationToggle() {
    const current = this._fxAnimation.value;
    return html`<div class="toggle-group" role="group">
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${current}
        data-testid="fx-animation-on"
        @click=${() => {
          setFxAnimationEnabled(true);
        }}
      >
        ${t("settings.appearance.canvas.fx.option.on")}
      </button>
      <button
        class="toggle-opt"
        type="button"
        aria-pressed=${!current}
        data-testid="fx-animation-off"
        @click=${() => {
          setFxAnimationEnabled(false);
        }}
      >
        ${t("settings.appearance.canvas.fx.option.off")}
      </button>
    </div>`;
  }

  /**
   * Sprint 24 A3 — D-24-12 closure: AYARLAR > Görünüm sub-tab
   * canvas/wire/symbol toggle'ları gerçek state'e bağlandı.
   * Sprint 23 F1'de canvas-prefs altyapı shipped, UI bağlama
   * Sprint 24'e ertelenmişti.
   */
  private renderCanvasStyleToggle() {
    const current = this._canvasStyle.value;
    const opts: { id: CanvasStyle; labelKey: TranslationKey }[] = [
      { id: "night", labelKey: "settings.appearance.canvas.background.option.night" },
      { id: "technical", labelKey: "settings.appearance.canvas.background.option.technical" },
      { id: "paper", labelKey: "settings.appearance.canvas.background.option.paper" },
      { id: "blank", labelKey: "settings.appearance.canvas.background.option.blank" },
    ];
    return html`<div class="toggle-group" role="group">
      ${opts.map(
        (o) => html`
          <button
            class="toggle-opt"
            type="button"
            aria-pressed=${current === o.id}
            data-testid="canvas-style-${o.id}"
            @click=${() => {
              setCanvasStyle(o.id);
            }}
          >
            ${t(o.labelKey)}
          </button>
        `,
      )}
    </div>`;
  }

  private renderWireCurveToggle() {
    const current = this._wireCurve.value;
    const opts: { id: WireCurve; labelKey: TranslationKey }[] = [
      { id: "catenary", labelKey: "settings.appearance.canvas.wire.option.catenary" },
      { id: "manhattan", labelKey: "settings.appearance.canvas.wire.option.manhattan" },
      { id: "linear", labelKey: "settings.appearance.canvas.wire.option.linear" },
    ];
    return html`<div class="toggle-group" role="group">
      ${opts.map(
        (o) => html`
          <button
            class="toggle-opt"
            type="button"
            aria-pressed=${current === o.id}
            data-testid="wire-curve-${o.id}"
            @click=${() => {
              setWireCurve(o.id);
            }}
          >
            ${t(o.labelKey)}
          </button>
        `,
      )}
    </div>`;
  }

  private renderSymbolStandardToggle() {
    const current = this._symbolStandard.value;
    const opts: { id: SymbolStandard; labelKey: TranslationKey }[] = [
      { id: "iec", labelKey: "settings.appearance.canvas.symbol.option.iec" },
      { id: "ansi", labelKey: "settings.appearance.canvas.symbol.option.ansi" },
    ];
    return html`<div class="toggle-group" role="group">
      ${opts.map(
        (o) => html`
          <button
            class="toggle-opt"
            type="button"
            aria-pressed=${current === o.id}
            data-testid="symbol-standard-${o.id}"
            @click=${() => {
              setSymbolStandard(o.id);
            }}
          >
            ${t(o.labelKey)}
          </button>
        `,
      )}
    </div>`;
  }

  private renderLanguage() {
    return html`<div data-testid="content-language">
      <h1 class="content-title">${t("settings.language.title")}</h1>
      <p class="content-sub">${t("settings.language.sub")}</p>

      <div class="group">
        <div class="group-title">${t("settings.language.locale.label")}</div>
        ${this.renderRow(
          t("settings.language.locale.row.title"),
          t("settings.language.locale.row.desc"),
          html`<div class="toggle-group" role="group">
            <button
              class="toggle-opt"
              type="button"
              aria-pressed=${this._locale.value === "tr"}
              data-testid="locale-tr"
              @click=${this._selectTr}
            >
              TR
            </button>
            <button
              class="toggle-opt"
              type="button"
              aria-pressed=${this._locale.value === "en"}
              data-testid="locale-en"
              @click=${this._selectEn}
            >
              EN
            </button>
          </div>`,
        )}
      </div>
    </div>`;
  }

  /**
   * Sprint 24 A3 closure — AYARLAR Simülasyon sub-tab gerçek
   * form-input bağlaması. Sprint 21 D-21-2 + Sprint 23 C1'de
   * persistence altyapısı (acDefaults / transientDefaults /
   * temperature) shipped, UI bağlama bu sprint'e kalmıştı.
   *
   * Read-only tolerance grubu Sprint 19+ "deferred" notunu
   * korur — bunlar engine'de hard-coded ngspice varsayılanları.
   */
  private renderSimulation() {
    const ac = this._ac.value;
    const tran = this._tran.value;
    const tempC = temperatureCelsius();
    return html`<div data-testid="content-simulation">
      <h1 class="content-title">${t("settings.simulation.title")}</h1>
      <p class="content-sub">${t("settings.simulation.sub")}</p>

      <div class="group">
        <div class="group-title">${t("settings.simulation.ac.label")}</div>
        ${this.renderRow(
          t("settings.simulation.ac.fStart.title"),
          t("settings.simulation.ac.fStart.desc"),
          this.renderFreqInput("ac-f-start", ac.fStart, (v) => {
            setACDefaults({ fStart: v });
          }),
        )}
        ${this.renderRow(
          t("settings.simulation.ac.fStop.title"),
          t("settings.simulation.ac.fStop.desc"),
          this.renderFreqInput("ac-f-stop", ac.fStop, (v) => {
            setACDefaults({ fStop: v });
          }),
        )}
        ${this.renderRow(
          t("settings.simulation.ac.points.title"),
          t("settings.simulation.ac.points.desc"),
          this.renderIntInput("ac-points", ac.points, 1, 1_000_000, (v) => {
            setACDefaults({ points: v });
          }),
        )}
        ${this.renderRow(
          t("settings.simulation.ac.sweep.title"),
          t("settings.simulation.ac.sweep.desc"),
          this.renderSweepToggle(ac.sweep),
        )}
      </div>

      <div class="group">
        <div class="group-title">${t("settings.simulation.tran.label")}</div>
        ${this.renderRow(
          t("settings.simulation.tran.tStop.title"),
          t("settings.simulation.tran.tStop.desc"),
          this.renderTimeInput("tran-t-stop", tran.tStop, (v) => {
            setTransientDefaults({ tStop: v });
          }),
        )}
        ${this.renderRow(
          t("settings.simulation.tran.maxStep.title"),
          t("settings.simulation.tran.maxStep.desc"),
          this.renderTimeInput("tran-max-step", tran.maxStep, (v) => {
            setTransientDefaults({ maxStep: v });
          }),
        )}
        ${this.renderRow(
          t("speed.settings.label"),
          t("speed.settings.description"),
          this.renderPlaybackSpeedSelect(),
        )}
      </div>

      <div class="group">
        <div class="group-title">${t("settings.simulation.temp.label")}</div>
        ${this.renderRow(
          t("settings.simulation.temp.row.title"),
          t("settings.simulation.temp.row.desc"),
          this.renderTemperatureInput(tempC),
        )}
      </div>

      <div class="group">
        <div class="group-title">${t("settings.simulation.tolerances.label")}</div>
        ${this.renderRow(
          t("settings.simulation.reltol.title"),
          t("settings.simulation.reltol.desc"),
          html`<span class="shortcut-keys">1e-3</span>`,
        )}
        ${this.renderRow(
          t("settings.simulation.abstol.title"),
          t("settings.simulation.abstol.desc"),
          html`<span class="shortcut-keys">1e-12</span>`,
        )}
        ${this.renderRow(
          t("settings.simulation.maxIter.title"),
          t("settings.simulation.maxIter.desc"),
          html`<span class="shortcut-keys">100</span>`,
        )}
      </div>

      <div class="group">
        ${this.renderRow(
          t("settings.simulation.reset.title"),
          t("settings.simulation.reset.desc"),
          html`<button
            class="toggle-opt"
            type="button"
            data-testid="sim-reset"
            title=${t("settings.simulation.reset.button")}
            @click=${() => {
              resetSimPrefs();
            }}
          >
            ${t("settings.simulation.reset.button")}
          </button>`,
        )}
      </div>
    </div>`;
  }

  private renderFreqInput(testId: string, value: number, onChange: (v: number) => void) {
    return html`<input
      class="num-input"
      type="number"
      step="any"
      min="0.001"
      max="1e9"
      .value=${String(value)}
      data-testid=${testId}
      @change=${(e: Event) => {
        const next = Number((e.target as HTMLInputElement).value);
        if (Number.isFinite(next) && next > 0) onChange(next);
      }}
    />`;
  }

  private renderTimeInput(testId: string, value: number, onChange: (v: number) => void) {
    return html`<input
      class="num-input"
      type="number"
      step="any"
      min="1e-15"
      .value=${String(value)}
      data-testid=${testId}
      @change=${(e: Event) => {
        const next = Number((e.target as HTMLInputElement).value);
        if (Number.isFinite(next) && next > 0) onChange(next);
      }}
    />`;
  }

  private renderIntInput(
    testId: string,
    value: number,
    min: number,
    max: number,
    onChange: (v: number) => void,
  ) {
    return html`<input
      class="num-input"
      type="number"
      step="1"
      min=${String(min)}
      max=${String(max)}
      .value=${String(value)}
      data-testid=${testId}
      @change=${(e: Event) => {
        const next = Math.round(Number((e.target as HTMLInputElement).value));
        if (Number.isFinite(next) && next >= min && next <= max) onChange(next);
      }}
    />`;
  }

  private renderSweepToggle(current: ACDefaults["sweep"]) {
    const opts: { id: ACDefaults["sweep"]; labelKey: TranslationKey }[] = [
      { id: "lin", labelKey: "settings.simulation.ac.sweep.lin" },
      { id: "oct", labelKey: "settings.simulation.ac.sweep.oct" },
      { id: "dec", labelKey: "settings.simulation.ac.sweep.dec" },
      { id: "log", labelKey: "settings.simulation.ac.sweep.log" },
    ];
    return html`<div class="toggle-group" role="group">
      ${opts.map(
        (o) => html`
          <button
            class="toggle-opt"
            type="button"
            aria-pressed=${current === o.id}
            data-testid="ac-sweep-${o.id}"
            @click=${() => {
              setACDefaults({ sweep: o.id });
            }}
          >
            ${t(o.labelKey)}
          </button>
        `,
      )}
    </div>`;
  }

  /**
   * Sprint 53 — playback default speed dropdown. Mirrors the toolbar
   * vxa-speed-control but rendered as a form select (AYARLAR row
   * style). Selecting an option calls setPlaybackSpeed() which
   * snaps, updates playbackSpeedSignal, and persists via mergeSettings.
   */
  private renderPlaybackSpeedSelect() {
    const active = this._playbackSpeed.value;
    return html`<select
      class="select-input"
      data-testid="playback-default-speed"
      .value=${String(active)}
      @change=${(e: Event) => {
        const next = Number((e.target as HTMLSelectElement).value);
        if (Number.isFinite(next)) setPlaybackSpeed(next);
      }}
    >
      ${SPEED_PRESETS.map(
        (speed) => html`
          <option value=${String(speed)} ?selected=${speed === active}>
            ${formatSpeedLabel(speed)}
          </option>
        `,
      )}
    </select>`;
  }

  private renderTemperatureInput(celsius: number) {
    return html`<input
      class="num-input"
      type="number"
      step="0.1"
      min=${String(TEMPERATURE_MIN_C)}
      max=${String(TEMPERATURE_MAX_C)}
      .value=${celsius.toFixed(2)}
      data-testid="temp-celsius"
      @change=${(e: Event) => {
        const next = Number((e.target as HTMLInputElement).value);
        if (Number.isFinite(next)) setTemperatureCelsius(next);
      }}
    />`;
  }

  private renderShortcuts() {
    /**
     * Sprint 23 D1+D2 — Custom shortcut overrides UI.
     *
     * Built-in default key combos (mockup-canonical, BLUEPRINT §13.3
     * subset). Override map'i `shortcutOverrides` signal'ından gelir;
     * UI override varsa onu, yoksa default'u gösterir.
     *
     * "Düzenle" butonu basit prompt() — kullanıcı tek satır key combo
     * (örn. "Mod+s" veya "Shift+P") girer. Conflict detect varsa
     * confirm dialog. Sprint 24+'da inline keyboard capture UI'sı
     * (event-based) replacement olur — Sprint 23 MVP prompt() yeterli.
     *
     * "Sıfırla" butonu (per-row, sadece override varsa görünür) →
     * clearOverride. "Tüm değişiklikleri sıfırla" footer →
     * resetAllOverrides.
     */
    const builtInDefaults: readonly {
      id: string;
      nameKey: TranslationKey;
      keys: readonly string[];
    }[] = [
      { id: "palette.open", nameKey: "settings.shortcuts.row.palette", keys: ["Mod+k"] },
      { id: "help.show", nameKey: "settings.shortcuts.row.help", keys: ["?"] },
      { id: "sim.run-dc", nameKey: "settings.shortcuts.row.runDc", keys: ["Mod+Shift+d"] },
      { id: "sim.run-tr", nameKey: "settings.shortcuts.row.runTr", keys: ["F5"] },
      {
        id: "sim.toggle-live",
        nameKey: "settings.shortcuts.row.toggleLive",
        keys: ["Shift+F5"],
      },
      { id: "view.waveform", nameKey: "settings.shortcuts.row.waveform", keys: ["F4"] },
      { id: "file.import", nameKey: "settings.shortcuts.row.import", keys: ["Mod+i"] },
      { id: "file.export", nameKey: "settings.shortcuts.row.export", keys: ["Mod+e"] },
      { id: "preset.show", nameKey: "settings.shortcuts.row.preset", keys: ["Mod+Shift+p"] },
      { id: "pane.switch", nameKey: "settings.shortcuts.row.pane", keys: ["1-5"] },
    ];

    const overrides = this._overrides.value;
    // Effective key map for conflict detection.
    const effectiveMap: Record<string, readonly string[]> = {};
    for (const def of builtInDefaults) {
      effectiveMap[def.id] = overrides[def.id] ?? def.keys;
    }

    const handleEdit = (commandId: string, current: readonly string[]): void => {
      const promptKey = t("settings.shortcuts.editPrompt");
      const input = window.prompt(promptKey, current.join(", "));
      if (input === null) return; // cancelled
      const trimmed = input.trim();
      // Empty string → disable (empty keys array).
      const newKeys =
        trimmed === ""
          ? []
          : trimmed
              .split(",")
              .map((k) => k.trim())
              .filter(Boolean);
      // Same as current → no-op.
      if (newKeys.join(",") === current.join(",")) return;
      const conflict = detectConflict(newKeys, commandId, effectiveMap);
      if (conflict !== null) {
        const confirmMsg = t("settings.shortcuts.conflict", { command: conflict });
        if (!window.confirm(confirmMsg)) return;
      }
      setOverride(commandId, newKeys);
    };

    const handleResetAll = (): void => {
      const confirmMsg = t("settings.shortcuts.resetConfirm");
      if (window.confirm(confirmMsg)) {
        resetAllOverrides();
      }
    };

    return html`<div data-testid="content-shortcuts">
      <h1 class="content-title">${t("settings.shortcuts.title")}</h1>
      <p class="content-sub">${t("settings.shortcuts.sub")}</p>
      <div class="group">
        <div class="group-title">${t("settings.shortcuts.list.label")}</div>
        ${builtInDefaults.map((def) => {
          const override = overrides[def.id];
          const effective = override ?? def.keys;
          const isOverridden = override !== undefined;
          const displayKeys =
            effective.length === 0 ? `(${t("settings.shortcuts.disabled")})` : effective.join(", ");
          return html`<div class="shortcut-row" data-cmd-id=${def.id}>
            <span class="shortcut-name"
              >${t(def.nameKey)}${isOverridden
                ? html`<span class="override-tag" title=${t("settings.shortcuts.overridden")}>
                    *</span
                  >`
                : nothing}</span
            >
            <span class="shortcut-keys">${displayKeys}</span>
            <button
              type="button"
              data-action="edit-shortcut"
              data-cmd-id=${def.id}
              class="shortcut-action"
              title=${t("settings.shortcuts.edit")}
              @click=${() => {
                handleEdit(def.id, effective);
              }}
            >
              ${t("settings.shortcuts.edit")}
            </button>
            ${isOverridden
              ? html`<button
                  type="button"
                  data-action="reset-shortcut"
                  data-cmd-id=${def.id}
                  class="shortcut-action"
                  title=${t("settings.shortcuts.reset")}
                  @click=${() => {
                    clearOverride(def.id);
                  }}
                >
                  ${t("settings.shortcuts.reset")}
                </button>`
              : nothing}
          </div>`;
        })}
      </div>
      <div class="group">
        <button
          type="button"
          class="shortcut-reset-all"
          data-action="reset-all-shortcuts"
          title=${t("settings.shortcuts.resetAll")}
          @click=${handleResetAll}
        >
          ${t("settings.shortcuts.resetAll")}
        </button>
      </div>
    </div>`;
  }

  private renderA11y() {
    return html`<div data-testid="content-a11y">
      <h1 class="content-title">${t("settings.a11y.title")}</h1>
      <p class="content-sub">${t("settings.a11y.sub")}</p>
      <div class="group">
        <div class="group-title">${t("settings.a11y.system.label")}</div>
        ${this.renderRow(
          t("settings.a11y.reduceMotion.title"),
          t("settings.a11y.reduceMotion.desc"),
          html`<div class="toggle-group" role="group">
            <button
              class="toggle-opt"
              type="button"
              aria-pressed=${this._reducedMotion.value ? "false" : "true"}
              @click=${() => {
                setReducedMotion(false);
              }}
            >
              ${t("settings.a11y.reduceMotion.option.off")}
            </button>
            <button
              class="toggle-opt"
              type="button"
              aria-pressed=${this._reducedMotion.value ? "true" : "false"}
              @click=${() => {
                setReducedMotion(true);
              }}
            >
              ${t("settings.a11y.reduceMotion.option.on")}
            </button>
          </div>`,
        )}
        ${this.renderRow(
          t("settings.a11y.contrast.title"),
          t("settings.a11y.contrast.desc"),
          html`<div class="toggle-group" role="group">
            <button
              class="toggle-opt"
              type="button"
              aria-pressed=${this._highContrast.value ? "false" : "true"}
              @click=${() => {
                setHighContrast(false);
              }}
            >
              ${t("settings.a11y.contrast.option.normal")}
            </button>
            <button
              class="toggle-opt"
              type="button"
              aria-pressed=${this._highContrast.value ? "true" : "false"}
              @click=${() => {
                setHighContrast(true);
              }}
            >
              ${t("settings.a11y.contrast.option.high")}
            </button>
          </div>`,
        )}
        ${this.renderRow(
          t("settings.a11y.fontSize.title"),
          t("settings.a11y.fontSize.desc"),
          html`<div class="toggle-group" role="group">
            ${FONT_SIZES.map(
              (size) =>
                html`<button
                  class="toggle-opt"
                  type="button"
                  aria-pressed=${this._fontSize.value === size ? "true" : "false"}
                  @click=${() => {
                    setFontSize(size);
                  }}
                >
                  ${size}
                </button>`,
            )}
          </div>`,
        )}
      </div>
    </div>`;
  }

  private renderAbout() {
    return html`<div data-testid="content-about">
      <h1 class="content-title">${t("settings.about.title")}</h1>
      <p class="content-sub">${t("settings.about.sub")}</p>

      <div class="about-card">
        <div class="about-card-label">${t("settings.about.version.label")}</div>
        <div class="about-card-title">
          v3 · Sprint ${VERSION.sprint} · ${t("settings.about.codename")}
        </div>
        <div class="about-card-body">${t("settings.about.version.body")}</div>
      </div>

      <div class="about-card">
        <div class="about-card-label">${t("settings.about.license.label")}</div>
        <div class="about-card-title">MIT</div>
        <div class="about-card-body">${t("settings.about.license.body")}</div>
      </div>

      <div class="about-card">
        <div class="about-card-label">${t("settings.about.studio.label")}</div>
        <div class="about-card-title">360 Studio · MMXXVI</div>
        <div class="about-card-body">${t("settings.about.studio.body")}</div>
      </div>

      <div class="row" style="border:none;">
        <div>
          <div class="name">${t("settings.about.cta.title")}</div>
          <div class="desc">${t("settings.about.cta.desc")}</div>
        </div>
        <button class="toggle-opt" type="button" @click=${this._goSimulator}>
          ${t("settings.about.cta.action")}
        </button>
      </div>
    </div>`;
  }

  /** Generic two-column row helper. */
  private renderRow(name: string, desc: string, control: unknown) {
    return html`<div class="row">
      <div>
        <div class="name">${name}</div>
        <div class="desc">${desc}</div>
      </div>
      <div>${control}</div>
    </div>`;
  }

  /** Visual-only toggle group — no wired state in B7. */
  private renderToggleGroup(opts: readonly string[], activeIndex: number) {
    return html`<div class="toggle-group" role="group">
      ${opts.map(
        (label, i) =>
          html`<button class="toggle-opt" type="button" aria-pressed=${i === activeIndex}>
            ${label}
          </button>`,
      )}
    </div>`;
  }

  private readonly _select = (id: SettingsSubTab): void => {
    this._subtab = id;
  };

  private readonly _selectTr = (): void => {
    if (this._locale.value !== "tr") toggleLocale();
  };

  private readonly _selectEn = (): void => {
    if (this._locale.value !== "en") toggleLocale();
  };

  private readonly _goSimulator = (): void => {
    setPane("simulator");
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-pane-settings": VxaPaneSettings;
  }
}
